import React, {useEffect} from "react";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "./ui/dialog";
import {Button} from "./ui/button";
import {Input} from "./ui/input";
import {Tooltip, TooltipContent, TooltipTrigger} from "./ui/tooltip";
import axios from "../utility/customAxios";
import AuthContext from "../contexts/JWTContext";
import {LoadingSpinner} from "./ui/customSpinner";
import {toast} from './ui/use-toast';

interface EventIngestionToken {
    uuid: string;
    serviceName: string;
    environment: string;
    token: string;
}

function EventIngestionForm({
                                onSuccess
                            }: {
    onSuccess: () => void
}) {
    const [serviceName, setServiceName] = React.useState("");
    const [environment, setEnvironment] = React.useState("");
    const [loading, setLoading] = React.useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        axios.post("/api/v1/eventToLog/create", {
            serviceName,
            environment,
        })
            .then(() => {
                toast({
                    className: "text-textlight",
                    title: "Success",
                    description: "Event ingestion token created successfully",
                });
                onSuccess();
            })
            .catch((e) => {
                console.error(e);
                let errorMessage = "Failed to create event ingestion token";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    className: "text-textlight",
                    title: "Failed to Create Token",
                    description: errorMessage,
                    variant: "destructive",
                });
            })
            .finally(() => setLoading(false));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="text-sm font-medium text-textdark">Service Name</label>
                <Input
                    className={"h-[36px]"}
                    required
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    placeholder="my-service"
                />
            </div>
            <div>
                <label className="text-sm font-medium text-textdark">Environment</label>
                <Input
                    className={"h-[36px]"}
                    required
                    value={environment}
                    onChange={(e) => setEnvironment(e.target.value)}
                    placeholder="production"
                />
            </div>
            <Button type="submit" disabled={loading}
                    className={"text-textmedium bg-primarytransparent border border-primary hover:text-textlight rounded"}>
                {loading ? "Creating..." : "Create Token"}
            </Button>
        </form>
    );
}

export function EventIngestionIntegration() {
    const [tokens, setTokens] = React.useState<EventIngestionToken[]>([]);
    const [openAddModal, setOpenAddModal] = React.useState(false);
    const [openListModal, setOpenListModal] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    useEffect(() => {
        loadTokens();
    }, []);

    const loadTokens = () => {
        setLoading(true);
        axios.get("/api/v1/eventToLog/list")
            .then((response) => {
                setTokens(response.data.tokens);
            })
            .catch((e) => {
                console.error(e);
                let errorMessage = "Failed to load event ingestion tokens";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    title: "Failed to Load Event Ingestion Tokens",
                    className: "text-textlight",
                    description: errorMessage,
                    variant: "destructive",
                });
            })
            .finally(() => setLoading(false));
    };

    const deleteToken = (uuid: string) => {
        axios.delete(`/api/v1/eventToLog/delete?uuid=${encodeURIComponent(uuid)}`)
            .then(() => {
                loadTokens();
                toast({
                    title: "Success",
                    className: "text-textlight",
                    description: "Event ingestion token deleted successfully",
                });
            })
            .catch((e) => {
                console.error(e);
                let errorMessage = "Failed to delete event ingestion token";
                if (e.response && e.response.status === 403) {
                    errorMessage = e.response.data.error;
                }
                toast({
                    title: "Failed to Delete Token",
                    className: "text-textlight",
                    description: errorMessage,
                    variant: "destructive",
                });
            });
    };

    return (
        <div className={"mt-4 p-4 bg-backgroundlight border rounded text-textmedium"}>
            <div className={"flex"}>
                <div className={"flex-grow"}>
                    <div className={"flex gap-1"}>
                        <div className="w-7 h-7 mr-1 text-primary flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                 className="w-5 h-5">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                        </div>
                        <h3 className={"text-lg font-bold"}>Event Ingestion</h3>
                    </div>
                    <p className={"text-sm text-textdark"}>
                        Convert arbitrary JSON events to logs that can be ingested by Metoro.{" "}
                        <a
                            href="https://metoro.io/docs/integrations/event-ingestion"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Learn more
                        </a>
                    </p>
                </div>
                <div className="space-x-2">
                    {true ? (
                        <>
                            <Button
                                requiredPermission={{
                                    resource: "integrations/default/*",
                                    operation: "read"
                                }}
                                onClick={() => setOpenListModal(true)}
                                className={"bg-primarytransparent border border-primary text-textlight hover:text-white"}
                            >
                                View Tokens
                            </Button>
                            <Button
                                onClick={() => setOpenAddModal(true)}
                                className={"bg-primarytransparent border border-primary text-textlight hover:text-white"}
                                requiredPermission={{
                                    resource: "integrations/default/*",
                                    operation: "create"
                                }}
                            >
                                Add Token
                            </Button>
                        </>
                    ) : (
                        <Tooltip delayDuration={100}>
                            <TooltipTrigger>
                                    <span>
                                        <Button
                                            disabled
                                            className={"bg-primarytransparent border border-primary text-textlight hover:text-white opacity-50 cursor-not-allowed"}
                                        >
                                            View Tokens
                                        </Button>
                                    </span>
                            </TooltipTrigger>
                            <TooltipContent side={"left"} className={"mr-2"}>
                                Only admins can view and manage tokens
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Add Token Dialog */}
            <Dialog open={openAddModal} onOpenChange={setOpenAddModal}>
                <DialogContent className={"w-1/2"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Add Event Ingestion Token</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Create a new token to ingest events as logs
                        </DialogDescription>
                    </DialogHeader>
                    <EventIngestionForm onSuccess={() => {
                        setOpenAddModal(false);
                        setOpenListModal(true);
                        loadTokens();
                    }}/>
                </DialogContent>
            </Dialog>

            {/* List Tokens Dialog */}
            <Dialog open={openListModal} onOpenChange={setOpenListModal}>
                <DialogContent className={"w-2/3 max-h-[66vh] overflow-y-auto"}>
                    <DialogHeader>
                        <DialogTitle className={"text-textmedium"}>Event Ingestion Tokens</DialogTitle>
                        <DialogDescription className={"text-textdark"}>
                            Manage your event ingestion tokens.
                            These tokens should be used in Authorization headers in your HTTP requests to Metoro.
                        </DialogDescription>
                    </DialogHeader>
                    {loading ? (
                        <div className="flex justify-center p-4">
                            <LoadingSpinner/>
                        </div>
                    ) : (
                        <div className="mt-4 space-y-4 min-h-0">
                            {tokens.length === 0 ? (
                                <p className="text-center text-sm text-textdark">No event ingestion tokens
                                    configured</p>
                            ) : (
                                <div className="space-y-4 h-full overflow-y-auto">
                                    {tokens.map((token) => (
                                        <div key={token.uuid} className="p-4 border rounded bg-backgroundlight">
                                            <div className="flex items-start justify-between">
                                                <div className="space-y-3 w-full">
                                                    <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                                                        <span className="text-sm font-medium text-textdark">Service Name:</span>
                                                        <span
                                                            className="text-sm text-textmedium">{token.serviceName}</span>
                                                    </div>
                                                    <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                                                        <span
                                                            className="text-sm font-medium text-textdark">Environment:</span>
                                                        <span
                                                            className="text-sm text-textmedium">{token.environment}</span>
                                                    </div>
                                                    <div className="grid grid-cols-[120px,1fr] gap-2 items-start">
                                                        <span
                                                            className="text-sm font-medium text-textdark">Token:</span>
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm text-textdark font-mono bg-backgrounddark p-2 rounded flex-grow">
                                                                {token.token.substring(0, 40)}
                                                                <span className="text-textdark/50">...</span>
                                                                {token.token.substring(token.token.length - 20)}
                                                            </p>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(token.token);
                                                                    toast({
                                                                        title: "Success",
                                                                        className: "text-textlight",
                                                                        description: "Token copied to clipboard",
                                                                    });
                                                                }}
                                                                className="border border-primary text-textlight hover:text-white bg-primarytransparent whitespace-nowrap"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                                     viewBox="0 0 24 24" fill="none"
                                                                     stroke="currentColor" strokeWidth="2"
                                                                     strokeLinecap="round" strokeLinejoin="round"
                                                                     className="w-4 h-4 mr-1">
                                                                    <rect x="9" y="9" width="13" height="13" rx="2"
                                                                          ry="2"/>
                                                                    <path
                                                                        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                                                </svg>
                                                                Copy
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <Button
                                                        requiredPermission={{
                                                            resource: "integrations/default/*",
                                                            operation: "delete"
                                                        }}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => deleteToken(token.uuid)}
                                                        className="border border-red-500 text-textlight hover:text-white bg-red-500/10"
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
} 